import { Currency } from '../types/api';

/**
 * Currency formatting, in one place.
 *
 * Two different questions get asked of money in this app and they have
 * different answers:
 *
 *   1. "What is in this account?"  -> the account's OWN currency. A USD
 *      chequing account holds US dollars; converting it for display invents a
 *      number the bank never showed and that changes daily.
 *   2. "What am I worth?"          -> the household's BASE currency, because
 *      adding USD to CAD to INR is meaningless until they are made commensurate.
 *
 * Reason: the danger is not a wrong number, it is a right number wearing the
 * wrong label. `$3,227.58` of USD sitting in a CAD-denominated column reads as
 * CAD to every human who sees it -- a ~38% error at today's rate, invisible
 * because nothing about the glyph is untrue. So every formatter here REQUIRES
 * an explicit currency argument. There is no default, and no overload that
 * lets a caller forget.
 */

/** Currencies a household may hold or report in. Mirrors the `Currency` type. */
export const SUPPORTED_CURRENCIES: readonly Currency[] = [
  'CAD',
  'USD',
  'EUR',
  'GBP',
  'INR',
  'AUD',
  'CHF',
  'CNY',
  'JPY',
] as const;

export const CURRENCY_LABELS: Record<Currency, string> = {
  CAD: 'Canadian Dollar',
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  INR: 'Indian Rupee',
  AUD: 'Australian Dollar',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan',
  JPY: 'Japanese Yen',
};

export const DEFAULT_BASE_CURRENCY: Currency = 'CAD';

/**
 * Locale chosen so the BASE currency renders with the bare `$` and every
 * foreign currency renders qualified.
 *
 * Reason: `en-CA` prints CAD as `$1,000.00` and USD as `US$1,000.00`; `en-US`
 * does the reverse. Picking the locale from the base currency is what makes
 * "the unqualified dollar sign is my currency" true rather than accidental.
 */
const LOCALE_FOR_BASE: Partial<Record<Currency, string>> = {
  CAD: 'en-CA',
  USD: 'en-US',
  GBP: 'en-GB',
  EUR: 'en-IE',
  INR: 'en-IN',
  AUD: 'en-AU',
};

function localeFor(base: Currency): string {
  return LOCALE_FOR_BASE[base] ?? 'en-CA';
}

export interface FormatOptions {
  /**
   * The household base currency, used only to pick the locale so that the
   * base currency is the one that gets the unqualified symbol.
   */
  base?: Currency;
  /** Decimal places. Defaults to 2, or 0 for JPY which has no minor unit. */
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  /** Drop the fractional part entirely -- for axis ticks and headline figures. */
  compact?: boolean;
}

/**
 * Format an amount in an explicitly named currency.
 *
 * @param amount   number, or a decimal string as the API returns it
 * @param currency the currency the amount is ALREADY denominated in; this
 *                 function never converts
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: Currency | string,
  opts: FormatOptions = {}
): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  const safe = Number.isFinite(num as number) ? (num as number) : 0;

  const code = (currency || DEFAULT_BASE_CURRENCY).toUpperCase();
  const base = opts.base ?? DEFAULT_BASE_CURRENCY;

  // JPY has no minor unit; showing ¥1,000.00 is simply wrong.
  const defaultDigits = code === 'JPY' ? 0 : 2;
  const digits = opts.compact
    ? 0
    : (opts.maximumFractionDigits ?? defaultDigits);

  try {
    return new Intl.NumberFormat(localeFor(base), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: opts.minimumFractionDigits ?? digits,
      maximumFractionDigits: digits,
    }).format(safe);
  } catch {
    // An unknown ISO code makes Intl throw. Falling back to a bare number
    // would strip the only thing identifying the currency, so keep the code.
    return `${code} ${safe.toFixed(digits)}`;
  }
}

/**
 * Format a total that has already been converted into the base currency.
 *
 * Distinct from formatCurrency only in intent -- it reads at the call site as
 * "this is a converted roll-up", which is the thing a reviewer needs to check.
 */
export function formatBase(
  amount: number | string | null | undefined,
  base: Currency,
  opts: Omit<FormatOptions, 'base'> = {}
): string {
  return formatCurrency(amount, base, { ...opts, base });
}

/**
 * True when a set of accounts spans more than one currency, meaning any total
 * over them had to be converted and should say so.
 */
export function isMixedCurrency(
  items: Array<{ currency?: Currency | string }>,
  base: Currency
): boolean {
  return items.some((i) => i.currency && i.currency !== base);
}

/** The bare symbol for a currency, for compact labels and axis titles. */
export function currencySymbol(currency: Currency | string, base: Currency = DEFAULT_BASE_CURRENCY): string {
  const code = (currency || base).toUpperCase();
  try {
    const parts = new Intl.NumberFormat(localeFor(base), {
      style: 'currency',
      currency: code,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

/**
 * Format a share quantity without inventing precision.
 *
 * Positions are stored at six decimal places because fractional shares are
 * real -- 6.20385 of VFV, 1.49528 of VOO. But rendering every position that
 * way gives "900.000000", which is noise on a whole-share holding and actively
 * misleading: six decimals implies a precision the number does not carry.
 *
 * So trailing zeros are dropped and genuine fractions kept: 900 stays 900,
 * 900.52 stays 900.52, and 6.20385 keeps every digit that means something.
 */
export function formatShares(value: number | string | null | undefined): string {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n as number)) return '0';
  const num = n as number;
  if (Number.isInteger(num)) return num.toLocaleString();
  // Up to six decimals, but only as many as the number actually uses.
  return num.toLocaleString(undefined, { maximumFractionDigits: 6 });
}
